/**
 * QuesturaTimer - Mostra il tempo rimanente per l'invio alla Questura
 * 
 * Logica: L'ospite fa check-in alle 14:00 ora italiana del giorno di arrivo.
 * La Questura concede tempo fino alle 23:59 ora italiana del giorno SUCCESSIVO.
 * Esempio: check-in 27 marzo → deadline 28 marzo alle 23:59 (ora italiana).
 * 
 * Il timer è SOLO INFORMATIVO e non blocca mai l'invio.
 */
import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

/**
 * Calcola l'offset UTC per l'ora italiana (CET/CEST) per una data specifica.
 * CET = UTC+1 (inverno), CEST = UTC+2 (estate, ultima domenica di marzo → ultima domenica di ottobre)
 */
const getItalyOffsetMinutes = (date) => {
  const year = date.getFullYear();
  // Ultima domenica di marzo
  const marchLast = new Date(Date.UTC(year, 2, 31));
  while (marchLast.getUTCDay() !== 0) marchLast.setUTCDate(marchLast.getUTCDate() - 1);
  marchLast.setUTCHours(1, 0, 0, 0); // Cambio ora alle 01:00 UTC

  // Ultima domenica di ottobre
  const octLast = new Date(Date.UTC(year, 9, 31));
  while (octLast.getUTCDay() !== 0) octLast.setUTCDate(octLast.getUTCDate() - 1);
  octLast.setUTCHours(1, 0, 0, 0); // Cambio ora alle 01:00 UTC

  if (date >= marchLast && date < octLast) {
    return 120; // CEST: UTC+2 = 120 minuti
  }
  return 60; // CET: UTC+1 = 60 minuti
};

/**
 * Crea una data UTC che corrisponde a un'ora specifica in Italia.
 * Es: toItalianTimeUTC('2026-03-27', 14, 0) → Date UTC per le 14:00 italiane del 27/03
 */
const toItalianTimeUTC = (dateStr, hours, minutes) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  // Crea la data in UTC con l'ora desiderata
  const tentative = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0));
  const offset = getItalyOffsetMinutes(tentative);
  // Sottrai l'offset per ottenere il momento UTC corrispondente
  return new Date(tentative.getTime() - offset * 60 * 1000);
};

const QuesturaTimer = ({ checkInDate, questuraSent, compact = false }) => {
  const [timeInfo, setTimeInfo] = useState(null);

  useEffect(() => {
    const calculateTime = () => {
      if (!checkInDate) return null;
      
      try {
        // Estrai la data di check-in (solo la parte YYYY-MM-DD)
        const dateStr = checkInDate.includes('T') ? checkInDate.split('T')[0] : checkInDate;
        const [year, month, day] = dateStr.split('-').map(Number);
        
        // Check-in convenzionale: 14:00 ora italiana del giorno di arrivo
        const checkinUTC = toItalianTimeUTC(dateStr, 14, 0);
        
        // Deadline: 23:59 ora italiana del giorno SUCCESSIVO
        // Usa Date() per gestire fine mese correttamente (es. 31 → 1 del mese dopo)
        const deadlineDate = new Date(year, month - 1, day + 1);
        const deadlineDateStr = `${deadlineDate.getFullYear()}-${String(deadlineDate.getMonth() + 1).padStart(2, '0')}-${String(deadlineDate.getDate()).padStart(2, '0')}`;
        const deadlineUTC = toItalianTimeUTC(deadlineDateStr, 23, 59);
        
        const now = new Date();
        const diffMs = deadlineUTC - now;
        const diffHours = diffMs / (1000 * 60 * 60);
        
        // Check-in non ancora avvenuto (prima delle 14:00 italiane del giorno di arrivo)
        if (now < checkinUTC) {
          return {
            status: 'future',
            message: 'Check-in non ancora iniziato',
            color: 'text-gray-400',
            bgColor: 'bg-gray-50',
            borderColor: 'border-gray-200'
          };
        }
        
        if (diffMs <= 0) {
          // Scaduto
          const hoursLate = Math.abs(Math.round(diffHours));
          return {
            status: 'expired',
            message: compact ? `${hoursLate}h` : `Scaduto da ${hoursLate} ore`,
            color: 'text-red-600',
            bgColor: 'bg-red-50',
            borderColor: 'border-red-300',
            hoursLate
          };
        }
        
        const hours = Math.floor(diffHours);
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        
        if (hours < 6) {
          // Urgente - meno di 6 ore
          return {
            status: 'urgent',
            message: compact ? `${hours}h ${minutes}m` : `${hours}h ${minutes}m rimanenti`,
            color: 'text-orange-600',
            bgColor: 'bg-orange-50',
            borderColor: 'border-orange-300',
            hours,
            minutes
          };
        }
        
        if (hours < 12) {
          // Attenzione - meno di 12 ore
          return {
            status: 'warning',
            message: compact ? `${hours}h ${minutes}m` : `${hours}h ${minutes}m rimanenti`,
            color: 'text-yellow-600',
            bgColor: 'bg-yellow-50',
            borderColor: 'border-yellow-300',
            hours,
            minutes
          };
        }
        
        // Ok - più di 12 ore
        return {
          status: 'ok',
          message: compact ? `${hours}h ${minutes}m` : `${hours}h ${minutes}m rimanenti`,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          hours,
          minutes
        };
        
      } catch (e) {
        console.error('QuesturaTimer error:', e);
        return null;
      }
    };

    setTimeInfo(calculateTime());
    
    // Update every minute
    const interval = setInterval(() => {
      setTimeInfo(calculateTime());
    }, 60000);
    
    return () => clearInterval(interval);
  }, [checkInDate]);

  // Se già inviato alla Questura
  if (questuraSent) {
    if (compact) {
      return (
        <span className="inline-flex items-center gap-1 text-green-600">
          <CheckCircle2 className="h-3 w-3" />
          <span className="text-xs">Inviato</span>
        </span>
      );
    }
    return (
      <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-green-50 border border-green-200">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <span className="text-sm font-medium text-green-700">Inviato alla Questura</span>
      </div>
    );
  }

  if (!timeInfo) return null;

  // Versione compatta (per liste)
  if (compact) {
    const Icon = timeInfo.status === 'expired' ? XCircle : 
                 timeInfo.status === 'urgent' ? AlertTriangle : 
                 timeInfo.status === 'future' ? Clock : Clock;
    
    return (
      <span className={`inline-flex items-center gap-1 ${timeInfo.color}`}>
        <Icon className="h-3 w-3" />
        <span className="text-xs font-medium">{timeInfo.message}</span>
      </span>
    );
  }

  // Versione estesa (per dettagli)
  const Icon = timeInfo.status === 'expired' ? XCircle : 
               timeInfo.status === 'urgent' ? AlertTriangle : Clock;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md ${timeInfo.bgColor} border ${timeInfo.borderColor}`}>
      <Icon className={`h-4 w-4 ${timeInfo.color}`} />
      <div className="flex flex-col">
        <span className={`text-sm font-medium ${timeInfo.color}`}>
          {timeInfo.message}
        </span>
        {timeInfo.status !== 'future' && (
          <span className="text-xs text-gray-500">
            Invio Questura
          </span>
        )}
      </div>
    </div>
  );
};

export default QuesturaTimer;
